import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';

import { IBuilding, ISearchResults, IUser, IZone, IZoneServiceItem } from 'models';
import { ROUTES } from 'consts';

import { SearchResultList } from './search-result-list';
import { Loading } from './loading';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';

interface SearchResultsProps {
  results: ISearchResults;
  onClick: Function;
  loading?: boolean;
}

export const SearchResults = ({ results, onClick, loading = false }: SearchResultsProps) => {
  const navigate = useNavigate();
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [hasResults, setHasResults] = React.useState(false);

  React.useEffect(() => {
    const hasResults = Object.values(results).reduce(
      (previousValue, currentValue) => previousValue + currentValue.length > 0,
      0,
    );
    setHasResults(hasResults > 0);
  }, [results]);

  const handleClick = (url: string) => {
    navigate(url);
    onClick();
  };

  return (
    <>
      {loading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loading text="Searching..." height={240} />
        </Box>
      )}

      {!loading && !hasResults && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 240,
          }}
        >
          <Search
            color="primary"
            sx={{
              fontSize: 40,
            }}
          />
          <Typography
            color="textSecondary"
            sx={{
              marginTop: 2,
            }}
          >
            No results found
          </Typography>
        </Box>
      )}

      {!loading && hasResults && (
        <>
          {/* Building Search Results */}
          <SearchResultList
            title="Buildings"
            data={results.buildings}
            formatter={(building: IBuilding) => building.address}
            onClick={(building: IBuilding) => handleClick(`/${orgSlug}/${ROUTES.BUILDINGS}/${building.id}`)}
          />

          {/* Zones Search Results */}
          <SearchResultList
            title="Zones"
            data={results.zones}
            formatter={(zone: IZone) => zone.name}
            onClick={(zone: IZone) =>
              handleClick(`/${orgSlug}/${ROUTES.BUILDINGS}/${zone.building_id}/${ROUTES.ZONES_ACTIVE}`)
            }
          />

          {/* service items Search Results */}
          <SearchResultList
            title="Service Items"
            data={results.service_items}
            formatter={(item: IZoneServiceItem) => item.name}
            onClick={() => handleClick(`/${orgSlug}/${ROUTES.ZONES}/${ROUTES.ZONES_ACTIVE}`)}
          />

          {/* users Search Results */}
          <SearchResultList
            title="Users"
            data={results.users}
            formatter={(user: IUser) => user.name}
            onClick={() => handleClick(`/${orgSlug}/${ROUTES.TEAM}`)}
          />
        </>
      )}
    </>
  );
};
